import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { X } from "lucide-react";

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const fadeOut = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(-20px);
    }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: ${fadeIn} 0.3s ease-out;
  &.fade-out {
    animation: ${fadeOut} 0.3s ease-out;
  }
`;

const Modal = styled.div`
  background-color: ${({ theme }): string => theme.colors.darkBg};
  color: ${({ theme }): string => theme.colors.yellow};
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.26);
  z-index: 100;
  animation: ${fadeIn} 0.3s ease-out;
  &.fade-out {
    animation: ${fadeOut} 0.3s ease-out;
  }
`;

const ModalHeader = styled.header`
  margin-bottom: ${({ theme }): string => theme.margins.medium};
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -30px;
  right: -10px;
  background: none;
  border: none;
  cursor: pointer;
  padding-right: 4px;
  color: ${({ theme }): string => theme.colors.yellow};
  opacity: 0.8;
  &:hover {
    opacity: 1;
  }
`;

const ModalContent = styled.section`
  margin-bottom: ${({ theme }): string => theme.margins.medium};
`;

const ModalActions = styled.section`
  display: flex;
  justify-content: center;
  gap: ${({ theme }): string => theme.margins.medium};
`;

interface UIModal {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions: React.ReactNode;
  modalStyles?: React.CSSProperties;
  headerStyles?: React.CSSProperties;
  titleStyles?: React.CSSProperties;
  hideCloseButton?: boolean;
}

const UIModal: React.FC<UIModal> = ({
  isOpen,
  onClose,
  title,
  children,
  actions,
  modalStyles,
  headerStyles,
  titleStyles,
  hideCloseButton,
}) => {
  const [isVisible, setIsVisible] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 250);
      return (): void => clearTimeout(timer);
    }
  }, [isOpen]);
  if (!isVisible) return null;
  return (
    <ModalOverlay className={!isOpen ? "fade-out" : ""} onClick={onClose}>
      <Modal
        style={modalStyles}
        className={!isOpen ? "fade-out" : ""}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader style={headerStyles}>
          <h2 style={titleStyles}>{title}</h2>
          {!hideCloseButton && (
            <CloseButton onClick={onClose} aria-label="Close">
              <X size={24} />
            </CloseButton>
          )}
        </ModalHeader>
        <ModalContent>{children}</ModalContent>
        <ModalActions>{actions}</ModalActions>
      </Modal>
    </ModalOverlay>
  );
};

export default UIModal;
